import Vue from 'vue'
import Vuex from 'vuex'

Vue.use(Vuex)

const VUEX_PROPERTIES = ['state', 'getters', 'actions', 'mutations']

let store = {};

(function updateModules () {
  // If store is an exported method = classic mode (deprecated)

  // Enforce store modules
  store.modules = store.modules || {}

  resolveStoreModules(require('../store/appSettings.ts'), 'appSettings.ts')
  resolveStoreModules(require('../store/campaign-analytics.ts'), 'campaign-analytics.ts')
  resolveStoreModules(require('../store/campaign-template.ts'), 'campaign-template.ts')
  resolveStoreModules(require('../store/campaign-test-sms.ts'), 'campaign-test-sms.ts')
  resolveStoreModules(require('../store/campaign.ts'), 'campaign.ts')
  resolveStoreModules(require('../store/client.ts'), 'client.ts')
  resolveStoreModules(require('../store/column.ts'), 'column.ts')
  resolveStoreModules(require('../store/contact-upload.ts'), 'contact-upload.ts')
  resolveStoreModules(require('../store/contact.ts'), 'contact.ts')
  resolveStoreModules(require('../store/credits.ts'), 'credits.ts')
  resolveStoreModules(require('../store/feature.ts'), 'feature.ts')
  resolveStoreModules(require('../store/growth-campaign.ts'), 'growth-campaign.ts')
  resolveStoreModules(require('../store/import-history.ts'), 'import-history.ts')
  resolveStoreModules(require('../store/inbox-graphql.ts'), 'inbox-graphql.ts')
  resolveStoreModules(require('../store/inbox.ts'), 'inbox.ts')
  resolveStoreModules(require('../store/list-assignment.ts'), 'list-assignment.ts')
  resolveStoreModules(require('../store/list.ts'), 'list.ts')
  resolveStoreModules(require('../store/manifest.ts'), 'manifest.ts')
  resolveStoreModules(require('../store/mmsTemplate.ts'), 'mmsTemplate.ts')
  resolveStoreModules(require('../store/page-navigation.ts'), 'page-navigation.ts')
  resolveStoreModules(require('../store/shopify-subscription.ts'), 'shopify-subscription.ts')
  resolveStoreModules(require('../store/shopify.ts'), 'shopify.ts')
  resolveStoreModules(require('../store/shopifyProduct.ts'), 'shopifyProduct.ts')
  resolveStoreModules(require('../store/subscription.ts'), 'subscription.ts')
  resolveStoreModules(require('../store/template.ts'), 'template.ts')
  resolveStoreModules(require('../store/upload.ts'), 'upload.ts')
  resolveStoreModules(require('../store/user-preference.ts'), 'user-preference.ts')
  resolveStoreModules(require('../store/user.ts'), 'user.ts')
  resolveStoreModules(require('../store/video-embed.ts'), 'video-embed.ts')
  resolveStoreModules(require('../store/admin/client.ts'), 'admin/client.ts')
  resolveStoreModules(require('../store/admin/optOutSettings.ts'), 'admin/optOutSettings.ts')
  resolveStoreModules(require('../store/admin/report.ts'), 'admin/report.ts')
  resolveStoreModules(require('../store/admin/template.ts'), 'admin/template.ts')
  resolveStoreModules(require('../store/admin/user.ts'), 'admin/user.ts')
  resolveStoreModules(require('../store/components/campaign-navigation.ts'), 'components/campaign-navigation.ts')
  resolveStoreModules(require('../store/components/contact-upload-navigation.ts'), 'components/contact-upload-navigation.ts')
  resolveStoreModules(require('../store/components/persistent-notification.ts'), 'components/persistent-notification.ts')
  resolveStoreModules(require('../store/components/session-management.ts'), 'components/session-management.ts')
  resolveStoreModules(require('../store/components/system-dialog.ts'), 'components/system-dialog.ts')
  resolveStoreModules(require('../store/components/toast.ts'), 'components/toast.ts')
  resolveStoreModules(require('../store/dashboard/summary.ts'), 'dashboard/summary.ts')

  // If the environment supports hot reloading...
})()

// createStore
export const createStore = store instanceof Function ? store : () => {
  return new Vuex.Store(Object.assign({
    strict: (process.env.NODE_ENV !== 'production')
  }, store))
}

function normalizeRoot (moduleData, filePath) {
  moduleData = moduleData.default || moduleData

  if (moduleData.commit) {
    throw new Error(`[nuxt] ${filePath} should export a method that returns a Vuex instance.`)
  }

  if (typeof moduleData !== 'function') {
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData)
  }
  return normalizeModule(moduleData, filePath)
}

function normalizeModule (moduleData, filePath) {
  if (moduleData.state && typeof moduleData.state !== 'function') {
    console.warn(`'state' should be a method that returns an object in ${filePath}`)

    const state = Object.assign({}, moduleData.state)
    // Avoid TypeError: setting a property that has only a getter when overwriting top level keys
    moduleData = Object.assign({}, moduleData, { state: () => state })
  }
  return moduleData
}

function resolveStoreModules (moduleData, filename) {
  moduleData = moduleData.default || moduleData
  // Remove store src + extension (./foo/index.js -> foo/index)
  const namespace = filename.replace(/\.(js|mjs|ts)$/, '')
  const namespaces = namespace.split('/')
  let moduleName = namespaces[namespaces.length - 1]
  const filePath = `store/${filename}`

  moduleData = moduleName === 'state'
    ? normalizeState(moduleData, filePath)
    : normalizeModule(moduleData, filePath)

  // If src is a known Vuex property
  if (VUEX_PROPERTIES.includes(moduleName)) {
    const property = moduleName
    const propertyStoreModule = getStoreModule(store, namespaces, { isProperty: true })

    // Replace state since it's a function
    mergeProperty(propertyStoreModule, moduleData, property)
    return
  }

  // If file is foo/index.js, it should be saved as foo
  const isIndexModule = (moduleName === 'index')
  if (isIndexModule) {
    namespaces.pop()
    moduleName = namespaces[namespaces.length - 1]
  }

  const storeModule = getStoreModule(store, namespaces)

  for (const property of VUEX_PROPERTIES) {
    mergeProperty(storeModule, moduleData[property], property)
  }

  if (moduleData.namespaced === false) {
    delete storeModule.namespaced
  }
}

function normalizeState (moduleData, filePath) {
  if (typeof moduleData !== 'function') {
    console.warn(`${filePath} should export a method that returns an object`)
    const state = Object.assign({}, moduleData)
    return () => state
  }
  return normalizeModule(moduleData, filePath)
}

function getStoreModule (storeModule, namespaces, { isProperty = false } = {}) {
  // If ./mutations.js
  if (!namespaces.length || (isProperty && namespaces.length === 1)) {
    return storeModule
  }

  const namespace = namespaces.shift()

  storeModule.modules[namespace] = storeModule.modules[namespace] || {}
  storeModule.modules[namespace].namespaced = true
  storeModule.modules[namespace].modules = storeModule.modules[namespace].modules || {}

  return getStoreModule(storeModule.modules[namespace], namespaces, { isProperty })
}

function mergeProperty (storeModule, moduleData, property) {
  if (!moduleData) {
    return
  }

  if (property === 'state') {
    storeModule.state = moduleData || storeModule.state
  } else {
    storeModule[property] = Object.assign({}, storeModule[property], moduleData)
  }
}
